
import React, { useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { EventFormValues } from '../schemas/eventFormSchema';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MapPin, ExternalLink } from 'lucide-react';

interface VenueInfoProps {
  form: UseFormReturn<EventFormValues>;
}

// Magyar vármegyék listája
const magyarVarmegyek = [
  'Budapest',
  'Bács-Kiskun',
  'Baranya',
  'Békés',
  'Borsod-Abaúj-Zemplén',
  'Csongrád-Csanád',
  'Fejér',
  'Győr-Moson-Sopron',
  'Hajdú-Bihar',
  'Heves',
  'Jász-Nagykun-Szolnok',
  'Komárom-Esztergom',
  'Nógrád',
  'Pest',
  'Somogy',
  'Szabolcs-Szatmár-Bereg',
  'Tolna',
  'Vas',
  'Veszprém',
  'Zala'
];

// Nagyobb városok és vármegyéik megfeleltetése
const varosVarmegyeMap: Record<string, string> = {
  'Budapest': 'Budapest',
  'Debrecen': 'Hajdú-Bihar',
  'Szeged': 'Csongrád-Csanád',
  'Miskolc': 'Borsod-Abaúj-Zemplén',
  'Pécs': 'Baranya',
  'Győr': 'Győr-Moson-Sopron',
  'Nyíregyháza': 'Szabolcs-Szatmár-Bereg',
  'Kecskemét': 'Bács-Kiskun',
  'Székesfehérvár': 'Fejér',
  'Szombathely': 'Vas',
  'Szolnok': 'Jász-Nagykun-Szolnok',
  'Tatabánya': 'Komárom-Esztergom',
  'Kaposvár': 'Somogy',
  'Sopron': 'Győr-Moson-Sopron',
  'Veszprém': 'Veszprém',
  'Békéscsaba': 'Békés',
  'Eger': 'Heves',
  'Zalaegerszeg': 'Zala',
  'Salgótarján': 'Nógrád',
  'Dunaújváros': 'Fejér',
  'Nagykanizsa': 'Zala',
  'Hódmezővásárhely': 'Csongrád-Csanád',
  'Esztergom': 'Komárom-Esztergom',
  'Gyöngyös': 'Heves',
  'Pápa': 'Veszprém',
  'Baja': 'Bács-Kiskun',
  'Siófok': 'Somogy'
};

const VenueInfo = ({ form }: VenueInfoProps) => {
  const isOnline = form.watch("isOnline");
  const cityValue = form.watch("city");
  
  // Store offline venue information when switching to online
  const [offlineVenueInfo, setOfflineVenueInfo] = React.useState({
    city: '',
    venueName: '',
    address: '',
    region: '',
    postalCode: '',
    registrationUrl: ''
  });

  // Log isOnline value for debugging
  useEffect(() => {
    console.log('isOnline value:', isOnline);
    console.log('eventUrl value:', form.getValues('eventUrl'));
  }, [isOnline, form]);

  // Autofill county based on city
  useEffect(() => {
    if (cityValue && !isOnline) {
      const normalizedCity = cityValue.trim();
      // Ha a város szerepel a listában, automatikusan kitöltjük a vármegyét
      if (varosVarmegyeMap[normalizedCity]) {
        form.setValue('region', varosVarmegyeMap[normalizedCity], { shouldDirty: true });
      }
    }
  }, [cityValue, form, isOnline]);

  // Handle toggle between online and offline
  useEffect(() => {
    const { unsubscribe } = form.watch((value, { name }) => {
      if (name === 'isOnline') {
        if (value.isOnline) {
          // Switching to online mode
          // Save current offline venue info
          setOfflineVenueInfo({
            city: form.getValues('city') || '',
            venueName: form.getValues('venueName') || '',
            address: form.getValues('address') || '',
            region: form.getValues('region') || '',
            postalCode: form.getValues('postalCode') || '',
            registrationUrl: form.getValues('registrationUrl') || ''
          });
          
          // Set online defaults
          const currentEventUrl = form.getValues('eventUrl');
          if (!currentEventUrl || currentEventUrl === '') {
            form.setValue('eventUrl', 'https://', { shouldDirty: true });
          }

          // Clear offline fields
          form.setValue('city', '', { shouldDirty: true });
          form.setValue('venueName', '', { shouldDirty: true });
          form.setValue('address', '', { shouldDirty: true });
          form.setValue('region', '', { shouldDirty: true });
          form.setValue('postalCode', '', { shouldDirty: true });
        } else {
          // Switching to offline mode
          // Restore offline venue info
          form.setValue('city', offlineVenueInfo.city, { shouldDirty: true });
          form.setValue('venueName', offlineVenueInfo.venueName, { shouldDirty: true });
          form.setValue('address', offlineVenueInfo.address, { shouldDirty: true });
          form.setValue('region', offlineVenueInfo.region, { shouldDirty: true });
          form.setValue('postalCode', offlineVenueInfo.postalCode, { shouldDirty: true });
          form.setValue('registrationUrl', offlineVenueInfo.registrationUrl, { shouldDirty: true });
          
          // Clear online URL only if switching back to offline and a default value is present
          if (form.getValues('eventUrl') === 'https://') {
            form.setValue('eventUrl', '', { shouldDirty: true });
          }
        }
      }
    });
    
    return () => unsubscribe();
  }, [form, offlineVenueInfo]);

  return (
    <Card className="border-2 border-medihub-primary/20">
      <CardHeader className="bg-medihub-primary/10 pb-2">
        <CardTitle className="text-lg flex items-center gap-2 text-medihub-primary">
          <MapPin className="h-5 w-5" />
          Helyszín
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4 space-y-4">
        <FormField
          control={form.control}
          name="isOnline"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
              <div className="space-y-0.5">
                <FormLabel>Online esemény</FormLabel>
                <FormDescription>
                  Online esemény vagy fizikai helyszín
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />

        {isOnline ? (
          <>
            <FormField
              control={form.control}
              name="eventUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Esemény URL-je <span className="text-red-500">*</span></FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="https://pelda.hu/esemeny" 
                      {...field} 
                      onChange={(e) => {
                        // Ensure URL starts with https:// if user deletes it
                        let url = e.target.value;
                        if (url === '') {
                          url = 'https://';
                        }
                        field.onChange(url);
                      }}
                    />
                  </FormControl>
                  <FormDescription>
                    Adja meg az online esemény elérési útját (https:// formátumban)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Regisztrációs URL mező online esetben is */}
            <FormField
              control={form.control}
              name="registrationUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-1">
                    <ExternalLink className="h-4 w-4" />
                    Regisztrációs link vagy információ
                  </FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="https://pelda.hu/regisztracio vagy kapcsolat@pelda.hu" 
                      {...field} 
                    />
                  </FormControl>
                  <FormDescription>
                    Adja meg a regisztrációs URL-t vagy kontakt információt, ahol a résztvevők jelentkezhetnek
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Város</FormLabel>
                    <FormControl>
                      <Input placeholder="Budapest" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="postalCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Irányítószám</FormLabel>
                    <FormControl>
                      <Input placeholder="1011" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="region"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Vármegye</FormLabel>
                  <Select 
                    onValueChange={field.onChange}
                    value={field.value || ""}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Válasszon vármegyét" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {magyarVarmegyek.map((varmegye) => (
                        <SelectItem key={varmegye} value={varmegye}>{varmegye}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    A város kiválasztásakor ez automatikusan kitöltődhet
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="venueName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Helyszín neve</FormLabel>
                  <FormControl>
                    <Input placeholder="Hotel Example" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cím</FormLabel>
                  <FormControl>
                    <Input placeholder="Példa utca 1." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Regisztrációs URL mező fizikai helyszín esetén is */}
            <FormField
              control={form.control}
              name="registrationUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-1">
                    <ExternalLink className="h-4 w-4" />
                    Rendezvény weboldala vagy regisztrációs információ
                  </FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="https://pelda.hu/rendezvenynev vagy kapcsolat@pelda.hu" 
                      {...field} 
                    />
                  </FormControl>
                  <FormDescription>
                    Adja meg a rendezvény weboldalát, regisztrációs linkjét vagy kapcsolatfelvételi adatait
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default VenueInfo;
